import { type QueryKey } from '@tanstack/react-query';
import { type ReactElement, useMemo, useState } from 'react';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import { StageExpandedRuns } from '@/features/pipelines/components/PipelineTableV2NestedRuns';
import {
  useDeletePipelineStage,
  useUpdatePipelineStage,
} from '@/features/pipelines/hooks/pipelines';
import { resolvePipelineSlug } from '@/features/pipelines/utils/pipelineSlug';
import { buildStageDetailPath } from '@/features/pipelines/utils/stagePaths';
import { useProjectFilter, useProjectMetricThresholds } from '@/features/projects';
import { DetailEditEntityDialog } from '@/features/shared/components/entityDetails/DetailEditEntityDialog';
import { TableActionsCell } from '@/features/shared/components/tableSystem/TableCells';
import { buildEntityMetricColgroup } from '@/features/shared/components/tableSystem/TableV2MetricColumnLayout';
import { TableV2MetricCells } from '@/features/shared/components/tableSystem/TableV2MetricColumns';
import { tableV2NestedCarrierRowSx } from '@/features/shared/components/tableSystem/TableV2NestedTable';
import {
  TableV2ActionsHeaderIcon,
  TableV2ChevronButton,
  TableV2DateTimeCell,
  TableV2HeaderLabel,
  TableV2NameCell,
  TableV2ProjectCell,
  TableV2SortableHeader,
  TableV2StatusChip,
  TableV2TagGroup,
  tableV2ActionsColumnWidth,
  tableV2ChevronCellSx,
  tableV2ChevronColumnWidth,
  type TableV2SkeletonColumnKind,
} from '@/features/shared/components/tableSystem/TableV2Primitives';
import { TableV2QueryShell } from '@/features/shared/components/tableSystem/TableV2QueryShell';
import { buildTableV2BodyRowSurfaceSx } from '@/features/shared/components/tableSystem/TableV2RowSurface';
import { useDetailEditEditor } from '@/features/shared/hooks/useDetailEditEditor';
import { buildTagColorByName } from '@/features/shared/tags';
import { useNotifications } from '@/providers/notificationHooks';
import { getEntityActionLabel } from '@/shared/copy/actions';
import { getDialogMessage } from '@/shared/copy/dialogs';
import { getToastMessage } from '@/shared/copy/toasts';
import { resolveVisibleMetricThresholdKeys } from '@/shared/utils/metricsThresholds';
import { s } from '@/theme/tokens';
import { TableCell, TableRow } from '@/ui/primitives';
import type { TagColor, TagOption } from '@/shared/data/tags';
import type { MetricThresholdGroupKey } from '@/shared/utils/metricsThresholds';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { MetricThresholdGroup, PipelineStageListItemDto, QueryResult } from '@flowqa/shared';

export type StageTableV2SortKey =
  | 'executionAverageDurationMs'
  | 'executionFlakeScore'
  | 'executionFrequencyPerDay'
  | 'executionPassRate'
  | 'stageName'
  | 'startedAt'
  | 'status';

const baseSkeletonColumns = ['chevron', 'name', 'status', 'dateTime', 'tags', 'actions'] as const;

const buildSkeletonColumns = (
  visibleMetricKeys: MetricThresholdGroupKey[],
  includeProject: boolean,
): TableV2SkeletonColumnKind[] => [
  ...baseSkeletonColumns.slice(0, 4),
  ...visibleMetricKeys.map(() => 'metric' as const),
  ...baseSkeletonColumns.slice(4, 5),
  ...(includeProject ? (['project'] satisfies TableV2SkeletonColumnKind[]) : []),
  ...baseSkeletonColumns.slice(5),
];

const buildColgroup = (
  visibleMetricKeys: MetricThresholdGroupKey[],
  includeProject: boolean,
): ReactElement => buildEntityMetricColgroup(visibleMetricKeys, includeProject);

type ProjectOption = { value: string; label: string };

type StagesTableV2Props = {
  active?: boolean;
  initialPageSize?: number;
  noRowsText: string;
  onQueryMeta?: (meta: { total: number; items: PipelineStageListItemDto[] } | null) => void;
  pageSizeOptions?: number[];
  projectOptions?: ProjectOption[];
  queryFn: (input: GridQueryInput) => Promise<QueryResult<PipelineStageListItemDto>>;
  queryKey: QueryKey;
  search?: string;
  showProjectColumn?: boolean;
  sort: ApiSort;
  stageRunsEndAt?: string | null;
  stageRunsStartAt?: string | null;
  tagOptionsOverride?: TagOption[];
  onSortChange: (sort: ApiSort) => void;
};

const StageRow = ({
  deleteStagePending,
  editPending,
  isLastVisibleRow,
  row,
  projectName,
  showProjectColumn,
  stageRunsEndAt,
  stageRunsStartAt,
  tagColorByName,
  thresholds,
  visibleMetricKeys,
  onDeleteRow,
  onEditRow,
}: {
  deleteStagePending: boolean;
  editPending: boolean;
  isLastVisibleRow: boolean;
  projectName: string | null;
  row: PipelineStageListItemDto;
  showProjectColumn: boolean;
  stageRunsEndAt: string | null;
  stageRunsStartAt: string | null;
  tagColorByName: Partial<Record<string, TagColor>>;
  thresholds: MetricThresholdGroup;
  visibleMetricKeys: MetricThresholdGroupKey[];
  onDeleteRow: (row: PipelineStageListItemDto) => void;
  onEditRow: (row: PipelineStageListItemDto) => void;
}): ReactElement => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <TableRow
        sx={buildTableV2BodyRowSurfaceSx({
          hasRenderedDivider: !expanded && !isLastVisibleRow,
        })}
      >
        <TableCell align="center" sx={tableV2ChevronCellSx}>
          <TableV2ChevronButton
            expanded={expanded}
            label={expanded ? 'Collapse stage runs' : 'Expand stage runs'}
            onClick={() => {
              setExpanded((current) => !current);
            }}
          />
        </TableCell>
        <TableCell>
          <TableV2NameCell
            href={buildStageDetailPath(row.id)}
            primary={row.stageName}
            secondary={row.pipelineName ?? null}
            secondaryHref={
              row.pipelineId && row.pipelineName
                ? `/pipelines/${resolvePipelineSlug({ id: row.pipelineId, name: row.pipelineName })}`
                : null
            }
          />
        </TableCell>
        <TableCell>
          <TableV2StatusChip status={row.status} />
        </TableCell>
        <TableCell>
          <TableV2DateTimeCell value={row.latestRun?.timestamp ?? row.lastUpdatedAt} />
        </TableCell>
        <TableV2MetricCells
          metrics={row.metrics ?? null}
          thresholds={thresholds}
          visibleMetricKeys={visibleMetricKeys}
        />
        <TableCell>
          <TableV2TagGroup tagColorByName={tagColorByName} tags={row.tags ?? []} />
        </TableCell>
        {showProjectColumn ? (
          <TableCell>
            <TableV2ProjectCell project={null} fallbackName={projectName} />
          </TableCell>
        ) : null}
        <TableCell align="center" sx={{ p: s('none'), width: tableV2ActionsColumnWidth }}>
          <TableActionsCell
            rowName={row.stageName}
            verticalAlign="center"
            editActionLabel={getEntityActionLabel('edit', 'Stage')}
            deleteActionLabel={getEntityActionLabel('delete', 'Stage')}
            showEditTagsAction={false}
            onEdit={() => {
              onEditRow(row);
            }}
            onDelete={() => {
              onDeleteRow(row);
            }}
            disableEdit={editPending || deleteStagePending}
            disableDelete={deleteStagePending}
          />
        </TableCell>
      </TableRow>
      <TableRow
        sx={[
          tableV2NestedCarrierRowSx,
          expanded && !isLastVisibleRow
            ? { '& > td': { borderBottom: (theme) => `1px solid ${theme.app.colors.border}` } }
            : {},
        ]}
      >
        <TableCell colSpan={buildSkeletonColumns(visibleMetricKeys, showProjectColumn).length}>
          <StageExpandedRuns
            childCount={row.executionTotal}
            expanded={expanded}
            organisationId={row.organisationId}
            pipelineId={row.pipelineId}
            projectId={row.projectId}
            stageName={row.stageName}
            stageRunsEndAt={stageRunsEndAt}
            stageRunsStartAt={stageRunsStartAt}
            tagColorByName={tagColorByName}
          />
        </TableCell>
      </TableRow>
    </>
  );
};

export const StagesTableV2 = ({
  active = true,
  initialPageSize = 10,
  noRowsText,
  onQueryMeta,
  onSortChange,
  pageSizeOptions = [10, 25, 50],
  projectOptions = [],
  queryFn,
  queryKey,
  search,
  showProjectColumn = false,
  sort,
  stageRunsEndAt = null,
  stageRunsStartAt = null,
  tagOptionsOverride = [],
}: StagesTableV2Props): ReactElement => {
  const { selectedProjectId } = useProjectFilter();
  const { notify } = useNotifications();
  const updateStage = useUpdatePipelineStage();
  const deleteStage = useDeletePipelineStage();
  const [editTarget, setEditTarget] = useState<PipelineStageListItemDto | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PipelineStageListItemDto | null>(null);
  const { thresholds } = useProjectMetricThresholds(selectedProjectId);
  const editEditor = useDetailEditEditor({
    projectId: editTarget?.projectId ?? null,
    successMessage: 'Stage updated',
    saveEntity: async ({ name }) => {
      if (!editTarget || !name) return;
      await updateStage.mutateAsync({
        id: editTarget.id,
        stageName: name,
        projectId: editTarget.projectId,
      });
    },
  });
  const visibleMetricKeys = useMemo(
    () => resolveVisibleMetricThresholdKeys(thresholds.stages),
    [thresholds.stages],
  );
  const projectNameById = useMemo(
    () => new Map(projectOptions.map((project) => [project.value, project.label])),
    [projectOptions],
  );
  const tagColorByName = useMemo(
    () => buildTagColorByName(tagOptionsOverride),
    [tagOptionsOverride],
  );
  const handleSortChange = (field: StageTableV2SortKey): void => {
    onSortChange({
      field,
      direction: sort.field === field && sort.direction === 'desc' ? 'asc' : 'desc',
    });
  };
  const header = (
    <TableRow>
      <TableCell sx={{ p: s('none'), width: tableV2ChevronColumnWidth }} />
      <TableCell>
        <TableV2SortableHeader
          activeSort={sort}
          field="stageName"
          label="Stage"
          onSortChange={handleSortChange}
        />
      </TableCell>
      <TableCell>
        <TableV2SortableHeader
          activeSort={sort}
          field="status"
          label="Latest Status"
          onSortChange={handleSortChange}
        />
      </TableCell>
      <TableCell>
        <TableV2SortableHeader
          activeSort={sort}
          field="startedAt"
          label="Ran At"
          onSortChange={handleSortChange}
        />
      </TableCell>
      {visibleMetricKeys.includes('passRate') ? (
        <TableCell sx={{ pl: s('none') }}>
          <TableV2SortableHeader
            activeSort={sort}
            field="executionPassRate"
            label="Success Rate"
            onSortChange={handleSortChange}
          />
        </TableCell>
      ) : null}
      {visibleMetricKeys.includes('frequency') ? (
        <TableCell sx={{ pl: s('none') }}>
          <TableV2SortableHeader
            activeSort={sort}
            field="executionFrequencyPerDay"
            label="Frequency"
            onSortChange={handleSortChange}
          />
        </TableCell>
      ) : null}
      {visibleMetricKeys.includes('avgDuration') ? (
        <TableCell sx={{ pl: s('none') }}>
          <TableV2SortableHeader
            activeSort={sort}
            field="executionAverageDurationMs"
            label="Avg. Duration"
            onSortChange={handleSortChange}
          />
        </TableCell>
      ) : null}
      {visibleMetricKeys.includes('flakeScore') ? (
        <TableCell sx={{ pl: s('none') }}>
          <TableV2SortableHeader
            activeSort={sort}
            field="executionFlakeScore"
            label="Flake Rate"
            onSortChange={handleSortChange}
          />
        </TableCell>
      ) : null}
      <TableCell>
        <TableV2HeaderLabel label="Tags" />
      </TableCell>
      {showProjectColumn ? (
        <TableCell>
          <TableV2HeaderLabel label="Project" />
        </TableCell>
      ) : null}
      <TableCell align="center" sx={{ p: s('none') }}>
        <TableV2ActionsHeaderIcon />
      </TableCell>
    </TableRow>
  );

  return (
    <>
      <TableV2QueryShell
        active={active}
        ariaLabel="Stages table"
        colgroup={buildColgroup(visibleMetricKeys, showProjectColumn)}
        emptyText={noRowsText}
        entityLabel="stages"
        entitySingularLabel="stage"
        header={header}
        initialPageSize={initialPageSize}
        onQueryMeta={onQueryMeta}
        pageSizeOptions={pageSizeOptions}
        queryFn={queryFn}
        queryKey={queryKey}
        renderRow={(row, { isLastVisibleRow }) => (
          <StageRow
            key={row.id}
            deleteStagePending={deleteStage.isPending}
            editPending={editEditor.isPending}
            isLastVisibleRow={isLastVisibleRow}
            projectName={projectNameById.get(row.projectId) ?? null}
            row={row}
            showProjectColumn={showProjectColumn}
            stageRunsEndAt={stageRunsEndAt}
            stageRunsStartAt={stageRunsStartAt}
            tagColorByName={tagColorByName}
            thresholds={thresholds.stages}
            visibleMetricKeys={visibleMetricKeys}
            onDeleteRow={setDeleteTarget}
            onEditRow={setEditTarget}
          />
        )}
        search={search}
        skeletonColumns={buildSkeletonColumns(visibleMetricKeys, showProjectColumn)}
        sorts={[sort]}
      />
      <DetailEditEntityDialog
        open={editTarget !== null}
        entityLabel="Stage"
        entityName={editTarget?.stageName ?? ''}
        nameValue={editTarget?.stageName ?? ''}
        projectId={editTarget?.projectId ?? null}
        loading={editEditor.isPending}
        cascadeCopy="Renaming this stage updates matching stage executions for the same pipeline."
        onClose={() => {
          if (editEditor.isPending) return;
          setEditTarget(null);
        }}
        onSave={async ({ name }) => {
          await editEditor.save({ name });
          setEditTarget(null);
        }}
      />
      <ConfirmDialog
        open={deleteTarget !== null}
        title={getDialogMessage('destructive.delete.title', { entity: 'stage' })}
        description="This stage will be deleted from the stage rollup view."
        confirmLabel={getDialogMessage('destructive.delete.confirmLabel', { entity: 'stage' })}
        confirmColor="red"
        loading={deleteStage.isPending}
        onClose={() => {
          if (deleteStage.isPending) return;
          setDeleteTarget(null);
        }}
        onConfirm={() => {
          if (!deleteTarget) return;
          deleteStage.mutate(
            { id: deleteTarget.id, projectId: deleteTarget.projectId },
            {
              onSuccess: () => {
                notify({
                  message: getToastMessage('entity.deleted', {
                    entity: 'stage',
                    name: deleteTarget.stageName,
                  }),
                  tone: 'green',
                });
                setDeleteTarget(null);
              },
            },
          );
        }}
      />
    </>
  );
};
